// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createContainer, type Action } from '@atlassian/react-sweet-state';
import { DENSITY_COMPACT, SORT_DESC } from '../../common/constants.tsx';
import type { State, Props } from './types.tsx';

const initialState: State = {
	density: DENSITY_COMPACT,
	isColumnResizingEnabled: true,
	isFixedTableLayoutEnabled: true,
	isInlineEditingEnabled: true,
	isInlineEditingExtendedFieldSupportEnabled: false,
	isIssueCreateEnabled: false,
	projectContext: null,
	isInfiniteScrollEnabled: false,
	isIssueRankingEnabled: false,
	isIssueRankDescending: false,
	isIssueHierarchyEnabled: false,
	isIssueOperationMeatballMenuEnabled: true,
	isRowReorderingEnabled: false,
	isEntireRowDraggable: false,
	isReparentingEnabled: false,
	isColumnBordersEnabled: true,
	isGridNavigationEnabled: false,
	isInlineFieldConfigEnabled: false,
	isInBetweenColumnPickerEnabled: false,
	isSingleLineRowHeightEnabled: false,
	isHideDoneItemsEnabled: false,
	isMergedColumnEnabled: false,
	isMergedColumnVisible: false,
	isChildIssuesTableVisualUpliftEnabled: false,
	isStickyColumnsEnabled: false,
	isStickyColumnsVisible: false,
	isAutoUnstickyOnSmallScreenEnabled: false,
	isConditionalFormattingEnabled: false,
	isTimelineEnabled: false,
	cellBorderStyle: 'FULL_BORDER',
	isProjectQuickstartOnboardingEnabled: false,
	isLinkRelationshipFieldEnabled: false,
	isRemoteLinkedIssuesEnabled: false,
	isEditableSyncLoaded: false,
	isOpenIssueLinkEnabled: true,
	isSkipIssueCreateSuccessFlagEnabled: false,
	isInBetweenStickyColumnPickerEnabled: false,
	isColumnConfigColumnEnabled: true,
	isRemoveColumnEnabled: true,
};

/**
 * Container to store the configuration state of various table features.
 * NOTE: Consider using `FeaturesContainer` from `./container.tsx` instead.
 * */
export const FeaturesContainerInternal = createContainer<Props>();

const updateStore: Action<State, Props> = (
	{ setState },
	{
		density,
		isColumnResizingEnabled,
		isInlineEditingEnabled,
		isIssueCreateEnabled,
		isInlineEditingExtendedFieldSupportEnabled,
		projectContext,
		isInfiniteScrollEnabled,
		sortDirection,
		sortField,
		isIssueHierarchyEnabled,
		isIssueOperationMeatballMenuEnabled,
		isRowReorderingEnabled,
		isEntireRowDraggable,
		isReparentingEnabled,
		jql,
		isColumnBordersEnabled,
		isGridNavigationEnabled,
		isInlineFieldConfigEnabled,
		isInBetweenColumnPickerEnabled,
		isSingleLineRowHeightEnabled,
		isHideDoneItemsEnabled,
		isMergedColumnEnabled,
		isMergedColumnVisible,
		isChildIssuesTableVisualUpliftEnabled,
		isIssueRankingEnabled,
		isStickyColumnsEnabled,
		isStickyColumnsVisible,
		isAutoUnstickyOnSmallScreenEnabled,
		isConditionalFormattingEnabled = false, // remove default false when 'jira_list_conditional_formatting' clean up
		isTimelineEnabled,
		cellBorderStyle,
		isProjectQuickstartOnboardingEnabled,
		isLinkRelationshipFieldEnabled,
		isRemoteLinkedIssuesEnabled,
		isEditableSyncLoaded,
		isOpenIssueLinkEnabled,
		isSkipIssueCreateSuccessFlagEnabled,
		isInBetweenStickyColumnPickerEnabled,
		isColumnConfigColumnEnabled,
		isRemoveColumnEnabled,
	},
) => {
	setState({
		density,
		isColumnResizingEnabled,
		isFixedTableLayoutEnabled: isColumnResizingEnabled,
		isInlineEditingEnabled,
		isInlineEditingExtendedFieldSupportEnabled,
		isIssueCreateEnabled: isIssueCreateEnabled && Boolean(projectContext),
		projectContext: projectContext ?? null,
		isInfiniteScrollEnabled,
		isIssueHierarchyEnabled: isIssueHierarchyEnabled && Boolean(projectContext),
		isIssueRankingEnabled,
		isIssueRankDescending: sortDirection === SORT_DESC,
		isIssueOperationMeatballMenuEnabled,
		isRowReorderingEnabled,
		isEntireRowDraggable,
		isReparentingEnabled,
		sortDirection,
		sortField,
		jql,
		isColumnBordersEnabled,
		isGridNavigationEnabled,
		isInlineFieldConfigEnabled,
		isInBetweenColumnPickerEnabled,
		isSingleLineRowHeightEnabled,
		isHideDoneItemsEnabled,
		isMergedColumnEnabled,
		isMergedColumnVisible,
		isChildIssuesTableVisualUpliftEnabled,
		isStickyColumnsEnabled,
		isStickyColumnsVisible,
		isAutoUnstickyOnSmallScreenEnabled,
		isConditionalFormattingEnabled,
		isTimelineEnabled,
		cellBorderStyle,
		isProjectQuickstartOnboardingEnabled,
		isLinkRelationshipFieldEnabled,
		isRemoteLinkedIssuesEnabled,
		isEditableSyncLoaded,
		isOpenIssueLinkEnabled,
		isSkipIssueCreateSuccessFlagEnabled,
		isInBetweenStickyColumnPickerEnabled,
		isColumnConfigColumnEnabled,
		isRemoveColumnEnabled,
	});
};

export const FeaturesStore = createStore({
	initialState,
	actions: {},
	containedBy: FeaturesContainerInternal,
	handlers: {
		onInit: () => updateStore,
		onContainerUpdate: () => updateStore,
	},
});
